import { MsalProvider } from "@azure/msal-react";
import { CssBaseline } from "@mui/material";
import { LicenseInfo } from "@mui/x-license";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { QueryClientProvider } from "@tanstack/react-query";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import App, { ConfigurationErrorApp } from "./App";
import { queryClient } from "./api/queryClient";
import { createAuthBootstrap } from "./authconfig";
import { getRuntimeConfig } from "./runtimeConfig/runtime-config";
import { ThemeProvider } from "./theme";
import "./index.css";

dayjs.extend(customParseFormat);

const muiLicense = getRuntimeConfig().PUBLIC_MUI_LICENSE;
console.log("muiLicense", muiLicense);
if (muiLicense) {
  LicenseInfo.setLicenseKey(muiLicense);
}

const rootEl = document.getElementById("root");

if (!rootEl) {
  throw new Error("Root element not found");
}

const root = ReactDOM.createRoot(rootEl);

const renderApp = (router: ReturnType<typeof createBrowserRouter>) => {
  root.render(
    <React.StrictMode>
      <ThemeProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <QueryClientProvider client={queryClient}>
            <CssBaseline enableColorScheme />
            <RouterProvider router={router} />
          </QueryClientProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </React.StrictMode>,
  );
};

const bootstrap = createAuthBootstrap();

if (bootstrap.status === "ready") {
  void bootstrap.instance
    .initialize()
    .then(() => {
      const router = createBrowserRouter([
        {
          path: "/*",
          element: (
            <MsalProvider instance={bootstrap.instance}>
              <App loginRequest={bootstrap.loginRequest} />
            </MsalProvider>
          ),
        },
      ]);

      renderApp(router);
    })
    .catch((error: unknown) => {
      const message =
        error instanceof Error
          ? `MSAL initialization failed: ${error.message}`
          : "MSAL initialization failed with an unknown error.";

      const router = createBrowserRouter([
        {
          path: "/*",
          element: <ConfigurationErrorApp message={message} />,
        },
      ]);

      renderApp(router);
    });
} else {
  const router = createBrowserRouter([
    {
      path: "/*",
      element: <ConfigurationErrorApp message={bootstrap.message} />,
    },
  ]);

  renderApp(router);
}
